import moment from "moment";
import { Moment } from "moment";
import {
  PlanDayResponseStatusEnum,
  RegisterRequestActivityLevelEnum,
  RegisterRequestDietGoalEnum,
  SubscriptionActionResponse,
  SubscriptionActionResponseTypeEnum,
} from "../openapi/client";
import {
  ExtremelyActiveIcon,
  ModeratelyActiveIcon,
  LightlyActiveIcon,
  SedentaryIcon,
  VeryActiveIcon,
  QatarIcon,
  SaudiArabiaIcon,
  UAEIcon,
  KuwaitIcon,
  BahrainIcon,
  OmanIcon,
} from "../assets/icons";

export const cdnUrl = process.env.REACT_APP_CDN_URL;

export const getSampleMenuUrl = (partnerSlug: string, planSlug: string, languageCode: string): string =>
  `${cdnUrl}/static/${partnerSlug}/plans/${planSlug}/sample-menu-${languageCode}.json`;
export const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
export const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
export const ga4TrackingId = process.env.REACT_APP_GA4_TRACKING_ID;

// Gulf Countries Phone Configuration
export const gulfCountries = [
  {
    code: "QA",
    name: "Qatar",
    dialCode: "+974",
    icon: QatarIcon,
    regex: /^[3567]{1}\d{7}$/,
    description: "Phone number should start with +974 and be followed by eight digits starting with 3, 5, 6 or 7",
  },
  {
    code: "SA",
    name: "Saudi Arabia",
    dialCode: "+966",
    icon: SaudiArabiaIcon,
    regex: /^5\d{8}$/,
    description: "Phone number should start with +966 and be followed by nine digits starting with 5",
  },
  {
    code: "AE",
    name: "United Arab Emirates",
    dialCode: "+971",
    icon: UAEIcon,
    regex: /^5[0245678]\d{7}$/,
    description:
      "Phone number should start with +971 and be followed by seven digits starting with 50, 52, 54, 55, 56, 57 or 58",
  },
  {
    code: "KW",
    name: "Kuwait",
    dialCode: "+965",
    icon: KuwaitIcon,
    regex: /^(5\d{7}|6\d{7}|9\d{7}|41\d{6})$/,
    description:
      "Phone number should start with +965 and be followed by 5, 6, 9 or 41 and total digits should be eight",
  },
  {
    code: "BH",
    name: "Bahrain",
    dialCode: "+973",
    icon: BahrainIcon,
    regex: /^(3\d{7}|6\d{7}|66\d{6})$/,
    description: "Phone number should start with +973 and be followed by 3, 6, 66 and total digits should be eight",
  },
  {
    code: "OM",
    name: "Oman",
    dialCode: "+968",
    icon: OmanIcon,
    regex: /^(90|91|92|95|96|97|98|99)\d{6}$/,
    description:
      "Phone number should start with +968 and be followed by eight digits starting with 90, 91, 92, 95, 96, 97, 98 or 99",
  },
];

export const maxFileSize = 10485760;

export const checkIf = (obj: any) => typeof obj !== "undefined" && obj !== null;
export const checkIfNull = (obj: any) => obj === null;
export const checkUndefined = (obj: any) => typeof obj === "undefined";
export const checkEmpty = (obj: any) => !checkIf(obj) || (typeof obj === "string" && obj.trim() === "");
export const checkEmptyObj = (obj: any) => Object.keys(obj).length === 0;
export const slugify = (text: string) => {
  return text
    .toLowerCase()
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "");
};
export const findImageByType = (imageObj: any, type: any) => {
  return imageObj?.find((io: any) => io.type === type)?.url;
};

// Native WebView bridge injected by the mobile apps.
declare global {
  interface Window {
    App?: {
      changeThemeColors?: (statusBarColor: string, bottomBarColor: string) => void;
    };
  }
}

// The mobile apps render a white bottom bar by default. Must be 6-digit hex —
// Android's Color.parseColor rejects 3-digit shorthand like "#fff".
const DEFAULT_BOTTOM_BAR_COLOR = "#ffffff";

/**
 * Sets the device status-bar / bottom-bar colors.
 * - Web: updates the <meta name="theme-color"> tag, where the browser honours it.
 * - Mobile apps: calls the App.changeThemeColors WebView bridge when present.
 * Runs on both so web and the native apps stay in sync. The bottom bar stays
 * white unless a screen overrides it. Colors are 6-digit hex strings.
 */
export const setThemeBarColors = (statusBarColor: string, bottomBarColor: string = DEFAULT_BOTTOM_BAR_COLOR) => {
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute("content", statusBarColor);
  }
  try {
    window.App?.changeThemeColors?.(statusBarColor, bottomBarColor);
  } catch (error) {
    // Native bridge missing or threw (e.g. plain web) — safe to ignore.
  }
};

export const debounce = (fn: Function, timeDelay = 1000) => {
  let fnTimer: NodeJS.Timeout;
  return (...args: any) => {
    clearTimeout(fnTimer);
    fnTimer = setTimeout(() => fn(...args), timeDelay);
  };
};

export const generateRandom = () => Math.round(10e10 * Math.random());

export const matchIsNumeric = (text: any) => {
  const isNumber = typeof text === "number";
  const isString = typeof text === "string";
  return isNumber || (isString && text !== "" && !isNaN(Number(text)));
};

export enum UserRoles {
  ADMIN = "ADMIN",
  MEMBER = "MEMBER",
  SUBSCRIBER = "SUBSCRIBER",
  ANONYMOUS = "ANONYMOUS",
}

export const getUserRole = (user: any) => {
  const isAdmin = user?.roles?.includes(UserRoles.ADMIN);
  const isMember = user?.roles?.includes(UserRoles.MEMBER);
  const isSubscriber = user?.roles?.includes(UserRoles.SUBSCRIBER);

  if (isAdmin) {
    return UserRoles.ADMIN;
  } else if (isSubscriber) {
    return UserRoles.SUBSCRIBER;
  } else if (isMember) {
    return UserRoles.MEMBER;
  } else return UserRoles.ANONYMOUS;
};

export const getStartDate = () => {
  let targetDate: Moment;
  const currentDate = moment().startOf("day");
  const currentDay = currentDate.format("dd");
  if (currentDay === "Th") {
    targetDate = currentDate.add(4, "days");
  } else if (currentDay === "Fr") {
    targetDate = currentDate.add(3, "days");
  } else {
    targetDate = currentDate.add(1, "days");
  }
  return targetDate;
};

export const disableDayCheck = (day: Moment) => {
  const higherLimit = moment().add(-9, "years");
  const lowerLimit = moment().add(-99, "years");
  return !day.isBetween(lowerLimit, higherLimit);
};

export const getAge = (date: string) => {
  const today = moment();
  const birthDate = moment(date);
  return today.diff(birthDate, "years");
};

export const translateError = (errorMessage: string) => {
  switch (errorMessage) {
    case "minDate":
      return "Outside of minimum allowed date";
    case "maxDate":
      return "Outside of maximum allowed date";
    case "invalidDate":
      return "Invalid Date";
    case "shouldDisableDate":
      return "Disabled Date";
    default:
      return errorMessage;
  }
};

export const findUniqueArray = (arr?: any[]) => {
  let temp: any = [];
  arr?.forEach((i: []) => {
    const including = temp.some((t: any) => t.join("") === i.join(""));
    if (!including) temp.push(i);
  });
  return temp;
};

export const areArraysEqual = (arr1: any[], arr2: any[]): boolean => {
  if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
  if (arr1.length !== arr2.length) return false;

  return arr1.every((item, index) => {
    if (Array.isArray(item) && Array.isArray(arr2[index])) {
      return areArraysEqual(item, arr2[index]);
    }
    return item === arr2[index];
  });
};

export const DAY_LIST = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];
export const defaultFormat = "YYYY-MM-DD";
export const monthFormat = "YYYY-MM";
export const displayFormat = "DD/MM/YYYY";
export const displayFormatForDateTime = "DD/MM/YYYY HH:mm";
export const displayFormatUX = "MMMM D, YYYY";

export const dietGoals = {
  [RegisterRequestDietGoalEnum.LoseWeight]: "Lose Weight",
  [RegisterRequestDietGoalEnum.Maintenance]: "Maintenance",
  [RegisterRequestDietGoalEnum.GainWeight]: "Gain Weight",
  [RegisterRequestDietGoalEnum.IncreaseMuscles]: "Increase Muscles",
};

export const activityLevels = {
  [RegisterRequestActivityLevelEnum.Sedentary]: "Sedentary",
  [RegisterRequestActivityLevelEnum.LightlyActive]: "Lightly Active",
  [RegisterRequestActivityLevelEnum.ModeratelyActive]: "Moderately Active",
  [RegisterRequestActivityLevelEnum.VeryActive]: "Very Active",
  [RegisterRequestActivityLevelEnum.ExtremelyActive]: "Extremely Active",
};

export const activityLevelOptions = {
  [RegisterRequestActivityLevelEnum.Sedentary]:
    "Little to no exercise, such as a desk job with no additional physical activity",
  [RegisterRequestActivityLevelEnum.LightlyActive]: "Light exercise, such as a daily walk, 1-3 days per week",
  [RegisterRequestActivityLevelEnum.ModeratelyActive]: "Moderate exercise, 3-5 days per week",
  [RegisterRequestActivityLevelEnum.VeryActive]: "Hard exercise, 6-7 days per week",
  [RegisterRequestActivityLevelEnum.ExtremelyActive]:
    "Hard daily exercise and a physical job or two times a day training",
};

export const activityLevelOptionsIcon = {
  [RegisterRequestActivityLevelEnum.Sedentary]: <SedentaryIcon />,
  [RegisterRequestActivityLevelEnum.LightlyActive]: <LightlyActiveIcon />,
  [RegisterRequestActivityLevelEnum.ModeratelyActive]: <ModeratelyActiveIcon />,
  [RegisterRequestActivityLevelEnum.VeryActive]: <VeryActiveIcon />,
  [RegisterRequestActivityLevelEnum.ExtremelyActive]: <ExtremelyActiveIcon />,
};

export const bmiScale = {
  underweight: { label: "Underweight", value: 18.5, text: "< 18.5", color: "#2677F1" },
  normal: { label: "Normal", value: 24.9, text: "18.5 - 24.9", color: "#0EAF54" },
  overweight: { label: "Overweight", value: 29.9, text: "25.0 - 29.9", color: "#F1A326" },
  obesity: { label: "Obesity", value: 30, text: "> 30.0", color: "#F65050" },
};

export const getBmiResult = (bmi: number) => {
  if (bmi < 18.5) {
    return "underweight";
  } else if (bmi >= 18.5 && bmi <= 24.9) {
    return "normal";
  } else if (bmi >= 25 && bmi <= 29.9) {
    return "overweight";
  } else if (bmi >= 30) {
    return "obesity";
  }
};

// Export new API cache utilities
export { cachedApiCall, clearApiCache, invalidateUserCache } from "./apiCache";

// Get initials from full name
export const getInitials = (fullName: string): string => {
  if (!fullName || typeof fullName !== "string") {
    return "";
  }

  const names = fullName
    .trim()
    .split(/\s+/)
    .filter((name) => name.length > 0);

  if (names.length === 0) {
    return "";
  }

  if (names.length === 1) {
    // Single name - return first letter
    return names[0][0].toUpperCase();
  }

  // Multiple names - return first letter of first and last name
  const firstInitial = names[0][0].toUpperCase();
  const lastInitial = names[names.length - 1][0].toUpperCase();

  return firstInitial + lastInitial;
};

export const RegistrationSteps = {
  findBestPlan: {
    goalSelected: "goal_selected",
    activityLevelSelected: "activity_level_selected",
    bodyMeasurementsEntered: "body_measurements_entered",
    medicalConditionsAnswered: "medical_conditions_answered",
    allergiesAnswered: "allergies_answered",
    dislikesAnswered: "dislikes_answered",
    personalInfoEntered: "personal_info_entered",
    phoneNumberEntered: "phone_number_entered",
    otpEntered: "otp_entered",
    registered: "registered",
  },
  joinNow: {
    phoneNumberEntered: "phone_number_entered",
    otpEntered: "otp_entered",
    personalInfoEntered: "personal_info_entered",
    goalSelected: "goal_selected",
    activityLevelSelected: "activity_level_selected",
    bodyMeasurementsEntered: "body_measurements_entered",
    medicalConditionsAnswered: "medical_conditions_answered",
    allergiesAnswered: "allergies_answered",
    dislikesAnswered: "dislikes_answered",
    registered: "registered",
  },
} as const;

export const SaleSteps = {
  planSelected: "plan_selected",
  packageSelected: "package_selected",
  daysSelected: "days_selected",
  addressEntered: "address_entered",
  proceededToCheckout: "proceeded_to_checkout",
  purchased: "purchased",
} as const;

// Google Analytics Event Tracking
export const GoogleAnalyticsEvents = {
  registrationFlow: {
    showRegister: "show_register",
    otpSent: "otp_sent",
    otpVerified: "otp_verified",
    passwordEntered: "password_entered",
    profileCreated: "profile_created",
    goalsSelected: "goals_selected",
    activitySelected: "activity_selected",
    measurementsSet: "measurements_set",
    addressEntered: "address_entered",
    medicalConditionsAdded: "medical_conditions_added",
    allergensAdded: "allergens_added",
    dislikesAdded: "dislikes_added",
    partnerSuggestionsAdded: "partner_suggestions_added",
    register: "register",
  },
  quickSaleFlow: {
    partnersViewed: "qs_partners_viewed",
    plansViewed: "qs_plans_viewed",
    packageSelected: "qs_package_selected",
    daysSelected: "qs_days_selected",
    addressEntered: "qs_address_entered",
    orderSummaryViewed: "qs_order_summary_viewed",
    register: "qs_register",
    purchaseCompleted: "qs_purchase_completed",
  },
  userSaleFlow: {
    partnersViewed: "partners_viewed",
    plansViewed: "plans_viewed",
    packageSelected: "package_selected",
    daysSelected: "days_selected",
    orderSummaryViewed: "order_summary_viewed",
    purchaseCompleted: "purchase_completed",
  },
};

// Date and Calendar Utilities

/**
 * Arabic locale text configuration for DatePicker components
 */
export const arabicLocaleText = {
  cancelButtonLabel: "إلغاء",
  okButtonLabel: "موافق",
  clearButtonLabel: "مسح",
  todayButtonLabel: "اليوم",
  toolbarTitle: "اختر التاريخ",
};

/**
 * Convert Western numerals (0-9) to Arabic-Indic numerals (٠-٩)
 * @param num - Number to convert
 * @returns String with Arabic-Indic numerals
 */
export const toArabicNumerals = (num: number): string => {
  const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
  return num
    .toString()
    .split("")
    .map((digit) => arabicNumerals[parseInt(digit)])
    .join("");
};

/**
 * Format a date for display with optional Arabic numeral conversion
 * @param date - Moment date object to format
 * @param isArabic - Whether to convert to Arabic numerals
 * @returns Formatted date string
 */
export const formatDateForDisplay = (date: Moment | null, isArabic: boolean): string => {
  if (!date || !date.isValid()) return "";

  // Format in English first to get Latin numbers
  const latinFormat = date.locale("en").format(displayFormat);

  // If Arabic, convert Latin numbers to Arabic numerals
  if (isArabic) {
    const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
    return latinFormat.replace(/\d/g, (digit) => arabicNumerals[parseInt(digit)]);
  }

  return latinFormat;
};

/**
 * A single API operation needed to resume (un-pause) one day of a subscription.
 * There is no per-day resume endpoint, so a paused range has to be carved up by
 * shrinking the existing PAUSE action and re-creating the remaining tail.
 */
export type ResumeDayOperation =
  | { kind: "delete"; actionId: string }
  | { kind: "update"; actionId: string; startDate: string; endDate?: string }
  | { kind: "create"; startDate: string; endDate?: string };

/**
 * All PAUSE actions of a subscription whose range covers the given day.
 * An action without an endDate is treated as an open ended pause.
 * @param actions - subscription actions (SubscriptionResponse.actions)
 * @param date - the day to look for, formatted with defaultFormat
 */
export const findPauseActionsForDate = (
  actions: SubscriptionActionResponse[] | undefined,
  date?: string
): SubscriptionActionResponse[] => {
  if (!checkIf(actions) || !checkIf(date)) return [];

  const target = moment(date, defaultFormat);
  if (!target.isValid()) return [];

  return (actions as SubscriptionActionResponse[]).filter((action) => {
    if (action.type !== SubscriptionActionResponseTypeEnum.Pause || !checkIf(action.startDate)) return false;

    const start = moment(action.startDate, defaultFormat);
    if (!start.isValid()) return false;

    if (!checkIf(action.endDate)) return target.isSameOrAfter(start, "day");

    const end = moment(action.endDate, defaultFormat);
    if (!end.isValid()) return target.isSameOrAfter(start, "day");

    return target.isBetween(start, end, "day", "[]");
  });
};

/**
 * Whether a delivery can still be skipped on a day with the given status.
 * Only days that are open for changes qualify, the delivery status bars gate their
 * own skip button on the same rule.
 */
export const canSkipDeliveryForStatus = (status?: PlanDayResponseStatusEnum): boolean =>
  status === PlanDayResponseStatusEnum.Selectable || status === PlanDayResponseStatusEnum.Selected;

/**
 * Whether any of the given PAUSE actions is continuous, meaning it has no end date.
 * Only the diet center can lift one, so the pause management entry points are hidden
 * while it is in place.
 */
export const hasContinuousPause = (actions?: SubscriptionActionResponse[]): boolean =>
  Boolean(
    actions?.some(
      (action) =>
        action.type === SubscriptionActionResponseTypeEnum.Pause &&
        checkIf(action.startDate) &&
        !checkIf(action.endDate)
    )
  );

/**
 * The operations needed to carve exactly one day out of a single PAUSE action.
 * The existing action is always shrunk before the tail is re-created, so the
 * backend never sees two overlapping PAUSE ranges.
 * A pause without an endDate is continuous and can only be lifted by the diet
 * center, so it is never resumable from here.
 * @returns an empty array when the action is continuous, unusable, or does not cover the day
 */
export const buildResumeSingleDayOperations = (
  action: SubscriptionActionResponse,
  date?: string
): ResumeDayOperation[] => {
  if (!checkIf(action?.id) || !checkIf(action?.startDate) || !checkIf(date)) return [];
  if (!checkIf(action.endDate)) return [];

  const actionId = String(action.id);
  const target = moment(date, defaultFormat);
  const start = moment(action.startDate, defaultFormat);
  const end = moment(action.endDate, defaultFormat);

  if (!target.isValid() || !start.isValid() || !end.isValid()) return [];
  if (target.isBefore(start, "day") || target.isAfter(end, "day")) return [];

  const startDate = start.locale("en").format(defaultFormat);
  const endDate = end.locale("en").format(defaultFormat);
  const previousDate = target.clone().subtract(1, "days").locale("en").format(defaultFormat);
  const nextDate = target.clone().add(1, "days").locale("en").format(defaultFormat);

  const isRangeStart = target.isSame(start, "day");
  const isRangeEnd = target.isSame(end, "day");

  // the pause covers this single day only - drop it entirely
  if (isRangeStart && isRangeEnd) return [{ kind: "delete", actionId }];

  // the day is the first one of the range - move the range one day forward
  if (isRangeStart) return [{ kind: "update", actionId, startDate: nextDate, endDate }];

  // the day is the last one of the range - move the range one day backward
  if (isRangeEnd) return [{ kind: "update", actionId, startDate, endDate: previousDate }];

  // the day sits inside the range - shrink the range, then re-pause the tail
  return [
    { kind: "update", actionId, startDate, endDate: previousDate },
    { kind: "create", startDate: nextDate, endDate },
  ];
};

/**
 * Medical conditions mapping for bilingual support
 * Maps condition keys to their English and Arabic translations
 */
const medicalConditionsMap: Record<string, { en: string; ar: string }> = {
  diabetic: { en: "diabetic", ar: "سكري" },
  hypothyroidism: { en: "hypothyroidism", ar: "قصور الغدة الدرقية" },
  hormonalImbalance: { en: "hormonal imbalance", ar: "اختلال هرموني" },
  insulinResistance: { en: "insulin resistance", ar: "مقاومة الإنسولين" },
  pcos: { en: "pcos", ar: "متلازمة تكيس المبايض" },
  hypertension: { en: "hypertension", ar: "ارتفاع ضغط الدم" },
  cholestremia: { en: "cholestremia", ar: "ارتفاع الكوليسترول" },
  highTriglycerides: { en: "high triglycerides", ar: "ارتفاع الدهون الثلاثية" },
  heartDiseases: { en: "heart diseases", ar: "أمراض القلب" },
  constipation: { en: "constipation", ar: "إمساك" },
  ulcers: { en: "ulcers", ar: "قرحة" },
  celiac: { en: "celiac", ar: "داء الزلاقي" },
  fattyLiver: { en: "fatty liver", ar: "دهون الكبد" },
  g6pd: { en: "g6pd", ar: "نقص إنزيم G6PD" },
  allergy: { en: "allergy", ar: "حساسية" },
  cancer: { en: "cancer", ar: "سرطان" },
  kidneyStones: { en: "kidney stones", ar: "حصى الكلى" },
  migraine: { en: "migraine", ar: "صداع نصفي" },
  gallstones: { en: "gallstones", ar: "حصى المرارة" },
  arthritis: { en: "arthritis", ar: "التهاب المفاصل" },
  pregnant: { en: "pregnant", ar: "حامل" },
  lactating: { en: "lactating", ar: "مرضعة" },
  vegetarian: { en: "vegetarian", ar: "نباتي" },
  vegan: { en: "vegan", ar: "نباتي صرف" },
};

/**
 * Check if a medical condition exists in the array (supports both English and Arabic)
 * @param conditions - Array of medical conditions (in any language)
 * @param conditionKey - Key of the condition to check (e.g., 'pregnant', 'pcos')
 * @returns boolean - true if the condition exists in either English or Arabic
 */
export const hasMedicalCondition = (conditions: string[], conditionKey: keyof typeof medicalConditionsMap): boolean => {
  if (!conditions || conditions.length === 0) return false;

  const mapping = medicalConditionsMap[conditionKey];
  if (!mapping) return false;

  // Normalize conditions array to lowercase for case-insensitive comparison
  const normalizedConditions = conditions.map((c) => c.toLowerCase().trim());

  return normalizedConditions.includes(mapping.en.toLowerCase()) || normalizedConditions.includes(mapping.ar);
};

/**
 * Check if any of multiple medical conditions exist in the array
 * @param conditions - Array of medical conditions (in any language)
 * @param conditionKeys - Keys of conditions to check
 * @returns boolean - true if any of the conditions exist
 */
export const hasAnyMedicalCondition = (
  conditions: string[],
  ...conditionKeys: (keyof typeof medicalConditionsMap)[]
): boolean => {
  return conditionKeys.some((key) => hasMedicalCondition(conditions, key));
};
